import { FocusTrap } from '@mui/base/FocusTrap';
import { Portal } from '@mui/base/Portal';
import { unstable_useModal as useModal } from '@mui/base/unstable_useModal';
import cn from 'classnames';
import { cloneElement, forwardRef, useCallback, useMemo } from 'react';

import ModalContext from '../modal-context';

import ModalTransition from './modal-transition';
import { getAnimationStateAttributes } from './modal-utils';
import { useModalPresence } from './use-modal-presence';

import type { ForwardedRef, HTMLAttributes, ReactElement } from 'react';

import type { ModalProps } from './modal.types';

import './modal.scss';

/** Props for the internal {@link Backdrop} component. */
interface BackdropProps {
  className?: string;
}

/**
 * Translucent overlay rendered behind the modal content.
 * Clicking it delegates to the modal's close handler via MUI Base.
 *
 * @internal
 */
const Backdrop = forwardRef<HTMLDivElement, BackdropProps>((props, ref) => {
  const { className, ...rest } = props;
  return <div className={cn('rbui-modal__backdrop', className)} ref={ref} {...rest} />;
});

Backdrop.displayName = 'Backdrop';

/**
 * Internal shape used to wire MUI Base transition lifecycle callbacks into
 * the cloned `ModalTransition` child.
 */
interface TransitionLifecycleProps {
  onEnter?: () => void;
  onExited?: () => void;
  tabIndex?: string;
}

/**
 * Modal component lets you create dialogs that force the user to take action
 * before continuing.
 *
 * The modal traps focus while open, locks the page scroll, and renders its
 * content inside a {@link Portal} so it sits above all other content in the
 * stacking context.
 *
 * ## Usage
 *
 * ```tsx
 * <Modal open>
 *   <ModalHeader>
 *     <ModalHeaderTitle>Modal header</ModalHeaderTitle>
 *   </ModalHeader>
 *   <ModalBody>
 *     Design systems bridge the gap between creativity and consistency,
 *     ensuring a harmonious user experience across platforms.
 *   </ModalBody>
 *   <ModalFooter>
 *     <ModalFooterActions>
 *       <ModalCloseButton>Cancel</ModalCloseButton>
 *       <BrButton onClick={() => {}} type="primary">Primary</BrButton>
 *     </ModalFooterActions>
 *   </ModalFooter>
 * </Modal>
 * ```
 */
const Modal = forwardRef((props: ModalProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
  const {
    children,
    className,
    container,
    disableAutoFocus = false,
    disableEnforceFocus = false,
    disablePortal = false,
    disableRestoreFocus = false,
    disableScrollLock = false,
    hideBackdrop = false,
    keepMounted = false,
    onClose,
    open,
    role = 'dialog',
    width = 'sm',
    ...rest
  } = props;

  const { animationState, handleAnimationStateChange, isMounted } = useModalPresence({ keepMounted, open });

  const contextValue = useMemo(
    () => ({
      onClose,
    }),
    [onClose],
  );

  const handleOnClose = useCallback((): void => {
    onClose?.();
  }, [onClose]);

  const transitionChild = (
    <ModalTransition in={open} onStateChange={handleAnimationStateChange} width={width}>
      {children}
    </ModalTransition>
  );

  const { getBackdropProps, getRootProps, getTransitionProps, hasTransition, isTopModal, portalRef } = useModal({
    children: transitionChild,
    closeAfterTransition: true,
    container,
    disableScrollLock,
    onClose: handleOnClose,
    open,
    rootRef: forwardedRef,
  });

  const rootClassName = cn('base-Modal-root', 'rbui-modal', className, {
    'base-Modal-hidden': !open && animationState === 'exited',
  });
  const childProps: TransitionLifecycleProps = {};
  const rootProps = getRootProps() as HTMLAttributes<HTMLDivElement>;
  const backdropProps = getBackdropProps() as HTMLAttributes<HTMLDivElement> & { open?: boolean };

  if (transitionChild.props.tabIndex === undefined) {
    childProps.tabIndex = '-1';
  }

  if (hasTransition) {
    const { onEnter, onExited } = getTransitionProps();

    childProps.onEnter = onEnter;
    childProps.onExited = onExited;
  }

  if (!isMounted) {
    return <></>;
  }

  return (
    <ModalContext.Provider value={contextValue}>
      <Portal container={container} disablePortal={disablePortal} ref={portalRef}>
        <div
          {...rest}
          {...rootProps}
          aria-modal="true"
          className={rootClassName}
          {...getAnimationStateAttributes(animationState)}
          role={role}
        >
          {!hideBackdrop && <Backdrop {...backdropProps} className="base-Modal-backdrop" />}
          <FocusTrap
            disableAutoFocus={disableAutoFocus}
            disableEnforceFocus={disableEnforceFocus}
            disableRestoreFocus={disableRestoreFocus}
            isEnabled={isTopModal}
            open={open}
          >
            {/* eslint-disable-next-line react/no-clone-element -- MUI Base transitions are wired through the direct child */}
            {cloneElement(transitionChild, childProps)}
          </FocusTrap>
        </div>
      </Portal>
    </ModalContext.Provider>
  );
});

Modal.displayName = 'Modal';

export default Modal;
